import './Stats.css';
import { formatDuration } from '../utils/formatters';
import { useMemo } from 'react';

const StatCard = ({ label, value, subtext }) => (
  <div className="stat-card">
    <span className="stat-card__label">{label}</span>
    <span className="stat-card__value">{value}</span>
    {subtext && <span className="stat-card__subtext">{subtext}</span>}
  </div>
);

const calculateTimePeriodStats = (sessions) => {
  if (!sessions || !sessions.length) {
    return { week: 0, month: 0, year: 0 };
  }

  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

  let week = 0, month = 0, year = 0;

  for (const session of sessions) {
    const sessionDate = new Date(session.date);
    const duration = session.duration || 0;

    if (sessionDate >= oneWeekAgo) week += duration;
    if (sessionDate >= oneMonthAgo) month += duration;
    if (sessionDate >= oneYearAgo) year += duration;
  }

  return { week, month, year };
};

const Stats = ({ data, history, loading }) => {
  const timePeriodStats = useMemo(
    () => calculateTimePeriodStats(history?.sessions),
    [history]
  );

  if (loading) {
    return (
      <div className="stats">
        <div className="stats__grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="stat-card stat-card--loading">
              <div className="skeleton skeleton--label" />
              <div className="skeleton skeleton--value" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { movies, streaks, seriesCompletions, favoriteMovie, totalWatchTime } = data;

  return (
    <div className="stats">
      <div className="stats__grid stats__grid--periods">
        <StatCard
          label="Last Week"
          value={formatDuration(timePeriodStats.week)}
        />
        <StatCard
          label="Last Month"
          value={formatDuration(timePeriodStats.month)}
        />
        <StatCard
          label="Last Year"
          value={formatDuration(timePeriodStats.year)}
        />
      </div>

      <div className="stats__grid">
        <StatCard
          label="Series Completions"
          value={seriesCompletions || 0}
          subtext="all 8 films"
        />
        <StatCard
          label="Current Streak"
          value={streaks?.current?.days ? `${streaks.current.days} days` : '—'}
        />
        <StatCard
          label="Total Watch Time"
          value={formatDuration(totalWatchTime)}
        />
      </div>

      {favoriteMovie && (
        <div className="stats__favorite">
          <span className="stats__favorite-label">Most Watched</span>
          <span className="stats__favorite-title">{favoriteMovie.title}</span>
          <span className="stats__favorite-count">{favoriteMovie.watchCount}× watched</span>
        </div>
      )}

      {movies && movies.length > 0 && (
        <div className="stats__movies">
          <h3 className="stats__movies-title">By Film</h3>
          <div className="stats__movies-list">
            {movies.map((movie) => (
              <div key={movie.index} className="movie-row">
                <span className="movie-row__index">{movie.index}</span>
                <span className="movie-row__title">{movie.title}</span>
                <span className="movie-row__count">{movie.watchCount}×</span>
                <span className="movie-row__time">{formatDuration(movie.totalDuration)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Stats;
